import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import DocumentLoading from './DocumentLoading';

const PDFViewer = React.lazy(() => import('./PDFViewer'));

type Props = {
  fileUrl: string | null;
  height?: number;
  isLoading?: boolean;
};

const SMALL_PADDING = 12;
const PADDING = 16;

function Document({ fileUrl, height = 300, isLoading }: Props) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [width, setWidth] = React.useState(0);
  const [numPages, setNumPages] = React.useState(0);

  const documentContainerRef = React.useRef<HTMLDivElement | null>(null);
  const padding = isSmall ? SMALL_PADDING : PADDING;

  React.useEffect(() => {
    const onResize = () => {
      if (documentContainerRef.current) {
        setWidth(documentContainerRef.current.offsetWidth);
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages: num }: { numPages: number }) => {
    setNumPages(num);
  };

  return (
    <Box
      ref={documentContainerRef}
      width="100%"
      height={height}
      sx={{
        overflowX: 'hidden',
        overflowY: 'auto',
      }}>
      {isLoading && <DocumentLoading height={height} />}
      {!!fileUrl && !isLoading && (
        <React.Suspense fallback={<DocumentLoading height={height} />}>
          <PDFViewer
            fileUrl={fileUrl}
            width={width}
            height={height}
            onLoadSuccess={onDocumentLoadSuccess}
            padding={padding}
            numPages={numPages}
          />
        </React.Suspense>
      )}
    </Box>
  );
}

export default Document;
